import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LoadingProgress from 'src/components/app/LoadingProgress';
import { useSnackbar } from 'notistack';
import $api from '../http';
import { useTranslation } from 'react-i18next';

export default function WidgetCodeDialog({
  open = false,
  onClose,
  currentItem
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState();
  const [config, setConfig] = useState({
    id: null,
    user_id: null,
    name: null,
    widget: null
  });
  const [error, setError] = useState({});
  const [widgetData, setWidgetData] = useState({});
  const [currentTab, setCurrentTab] = useState(0);

  const { enqueueSnackbar } = useSnackbar();

  const loadContent = () => {
    setIsLoading(true);
    if (config.uid) {
      $api.get(`/config/get-widget-code?uid=${config.uid}`)
        .catch((e) => {
          setError(e);
          setIsLoading(false);
          return e;
        })
        .then((response) => {
          setIsLoading(false);
          return response.data;
        })
        .then((data) => {
          if (data) {
            setIsLoading(false);
            setWidgetData(data);
          }
        });
    }
  };

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (isOpen) {
      loadContent();
    }
  }, [isOpen]);

  useEffect(() => {
    setConfig(currentItem);
  }, [currentItem]);

  const handleClose = () => {
    setIsOpen(false);
    setError({});
    onClose();
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleCopyClick = () => {
    let text = '';
    if (currentTab === 0) {
      text = `<script>(function(d, s, id){
  var js, fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) {return;}
  js = d.createElement(s); js.id = id;
  js.src = '${widgetData.widgetScriptURL}?r=' + encodeURIComponent(window.location);
  fjs.parentNode.insertBefore(js, fjs);
}(document, 'script', 'contactus-jssdk'));</script>`;
    } else if (currentTab === 1) {
      text = `<script>(function(d, s, id){
  var js, fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) {return;}
  js = d.createElement(s); js.id = id;
  js.src = '${widgetData.livechatScriptURL}?r=' + encodeURIComponent(window.location);
  fjs.parentNode.insertBefore(js, fjs);
}(document, 'script', 'contactus-jssdk'));</script>`;
  } else if (currentTab === 2) {
    text = `<script>var anychatWidget = window.anychatWidget || {
      mainButton: true,
      widgetID: '${widgetData.widgetUID}',
      apiKey: '${widgetData.apiKey}',
      showNewMessagePopup: true,
      moduleCongigUrl: '${widgetData.baseURL}/app'
    };
    (function(d, s, id){
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
          return;
      }
      js = d.createElement(s); js.id = id;
      js.src = '${widgetData.adminScriptURL}?r=' + encodeURIComponent(window.location);
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'contactus-jssdk'));
  </script>`;
  }
    navigator.clipboard.writeText(text).then(() => {
      enqueueSnackbar(t('Widget code has been copied to clipboard'), {
        variant: 'success'
      });
    }, () => {
      enqueueSnackbar(t('Widget code has not been copied to clipboard. Please use browser functionality.'), {
        variant: 'error'
      });
    });
  };

  const handleCopyWidgetClick = () => {
    navigator.clipboard.writeText(config.uid).then(() => {
      enqueueSnackbar(t('Widget ID has been copied to clipboard'), {
        variant: 'success'
      });
    }, () => {
      enqueueSnackbar(t('Widget ID has not been copied to clipboard. Please use browser functionality.'), {
        variant: 'error'
      });
    });
  };

  const handleCopyDirectLinkClick = () => {
    const val = widgetData.livechatDirectLink;
    navigator.clipboard.writeText(val).then(() => {
      enqueueSnackbar(t('Direct link copied to clipboard'), {
        variant: 'success'
      });
    }, () => {
      enqueueSnackbar(t('Direct link has not been copied to clipboard. Please use browser functionality.'), {
        variant: 'error'
      });
    });
  };

  const handleCopyEmbedLinkClick = () => {
    const val = widgetData.livechatEmbedLink;
    navigator.clipboard.writeText(val).then(() => {
      enqueueSnackbar(t('Embed link copied to clipboard'), {
        variant: 'success'
      });
    }, () => {
      enqueueSnackbar(t('Embed link has not been copied to clipboard. Please use browser functionality.'), {
        variant: 'error'
      });
    });
  };

  if (isLoading) {
    return (<LoadingProgress open />);
  }

  return (
    <>
      {(Object.keys(config).length !== 0) && (
        <Dialog
          open={isOpen}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="md"
        >
          <DialogTitle id="form-dialog-title">{t('Widget script code')}</DialogTitle>
          <DialogContent>
            <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
              <Tab value={0} {...a11yProps(0)} label={t('Menu button widget')} />
              <Tab value={1} {...a11yProps(1)} label={t('Standalone livechat widget')} />
              <Tab value={2} {...a11yProps(2)} label={t('Admin widget')} />
            </Tabs>
            {(currentTab === 0) && (
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1, backgroundColor: '#e4e4e4', borderRadius: 1, mt: 1, display: {
                    xs: 'none',
                    sm: 'block'
                  } }}>
                    <img style={{ maxWidth: '100%', height: 'auto' }} width="1154" height="1681" src="https://api.anychat.one/img/menu-button-mockup.png" alt="Menu button widget" />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Box>
                      <TextField
                        autoFocus
                        margin="dense"
                        label={t('Menu button widget code')}
                        type="text"
                        multiline
                        rows="10"
                        value={`<script>(function(d, s, id){
  var js, fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) {return;}
  js = d.createElement(s); js.id = id;
  js.src = '${widgetData.widgetScriptURL}?r=' + encodeURIComponent(window.location);
  fjs.parentNode.insertBefore(js, fjs);
}(document, 'script', 'contactus-jssdk'));</script>`}
                        helperText={error.name}
                        fullWidth
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyClick}
                        sx={{ mt: 1 }}
                      >
                        {t('Copy code')}
                      </Button>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        autoFocus
                        margin="dense"
                        label={t('Widget ID')}
                        type="text"
                        value={config.uid}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="copy"
                                onClick={handleCopyWidgetClick}
                                edge="end"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                </Grid>
              </Grid>
            )}
            {(currentTab === 1) && (
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1, backgroundColor: '#e4e4e4', borderRadius: 1, mt: 1, display: {
                    xs: 'none',
                    sm: 'block'
                  } }}>
                    <img style={{ maxWidth: '100%', height: 'auto' }} width="1154" height="1681" src="https://api.anychat.one/img/livechat-mockup.png" alt="Standalone livechat widget" />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Box>
                      <TextField
                        margin="dense"
                        label={t('Standalone livechat widget code')}
                        type="text"
                        multiline
                        rows="10"
                        value={`<script>(function(d, s, id){
  var js, fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) {return;}
  js = d.createElement(s); js.id = id;
  js.src = '${widgetData.livechatScriptURL}?r=' + encodeURIComponent(window.location);
  fjs.parentNode.insertBefore(js, fjs);
}(document, 'script', 'contactus-jssdk'));</script>`}
                        fullWidth
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyClick}
                        sx={{ mt: 1 }}
                      >
                        {t('Copy code')}
                      </Button>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                      <TextField
                        margin="dense"
                        label={t('Live chat direct link')}
                        type="text"
                        value={widgetData.livechatDirectLink}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="copy"
                                onClick={handleCopyDirectLinkClick}
                                edge="end"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                      <TextField
                        margin="dense"
                        label={t('Live chat embed link')}
                        type="text"
                        value={`${widgetData.livechatEmbedLink}`}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="copy"
                                onClick={handleCopyEmbedLinkClick}
                                edge="end"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                      <TextField
                        margin="dense"
                        label={t('Widget ID')}
                        type="text"
                        value={config.uid}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="copy"
                                onClick={handleCopyWidgetClick}
                                edge="end"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                  </Box>
                </Grid>
              </Grid>
            )}
            {(currentTab === 2) && (
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1, backgroundColor: '#e4e4e4', borderRadius: 2, mb: 2 }}>
                    <Typography>
                      {t("Admin widget allows you to display a chat in your site's admin area.")}&nbsp;
                      {t("You can use it to respond to messages directly from your site's admin area without visiting the anychat.one dashboard.")}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, backgroundColor: '#e4e4e4', borderRadius: 2, mb: 2 }}>
                    <Typography color="error">
                      {t('Please insert this code into the password-protected admin area of your site.')}&nbsp;
                      {t('No one should know your API Key.')}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, backgroundColor: '#e4e4e4', borderRadius: 2, mb: 2 }}>
                    <Typography color="success.main">
                      {t("You don't need to use this code if you're using our WordPress, PrestaShop, or OpenCart plugin.")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Box>
                      <TextField
                        margin="dense"
                        label={t('Admin widget code')}
                        type="text"
                        multiline
                        rows="10"
                        value={`<script>
  var anychatWidget = window.anychatWidget || {
    mainButton: true,
    widgetID: '${widgetData.widgetUID}',
    apiKey: '${widgetData.apiKey}',
    showNewMessagePopup: true,
    moduleCongigUrl: '${widgetData.baseURL}/app'
  };
  (function(d, s, id){
    var js, fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) {
        return;
    }
    js = d.createElement(s); js.id = id;
    js.src = '${widgetData.adminScriptURL}?r=' + encodeURIComponent(window.location);
    fjs.parentNode.insertBefore(js, fjs);
  }(document, 'script', 'contactus-jssdk'));
</script>`}
                        fullWidth
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyClick}
                        sx={{ mt: 1 }}
                      >
                        {t('Copy code')}
                      </Button>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        autoFocus
                        margin="dense"
                        label={t('Widget ID')}
                        type="text"
                        value={config.uid}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="copy"
                                onClick={handleCopyWidgetClick}
                                edge="end"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              {t('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

WidgetCodeDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  currentItem: PropTypes.object
};
